import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  FormFeedback,
  Form,
  Badge,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuthStore } from "../../stores/useAuthStore";
import { useLayoutStore } from "../../stores/useLayoutStore";

const UserProfile = () => {
  const { user } = useAuthStore();
  const layoutModeType = useLayoutStore((s) => s.layoutModeType);
  const isDarkMode = layoutModeType === "dark";

  const [loading, setLoading] = useState(false);

  // Récupération des données utilisateur
  const userName: string = (user?.username as string) || (user?.name as string) || (user?.first_name as string) || "Utilisateur";
  const userEmail: string = (user?.email as string) || "";
  const userId: string | number = (user?.userId as string | number) || (user?._id as string | number) || (user?.id as string | number) || "N/A";
  const userRoles = user?.roles || [];
  const userPhone: string = (user?.mobile as string) || (user?.phone as string) || "";
  const userFirstName: string = (user?.first_name as string) || ((user?.name as string)?.split(" ")[0] || "");
  const userLastName: string = (user?.last_name as string) || ((user?.name as string)?.split(" ").slice(1).join(" ") || "");

  // Génération des initiales pour l'avatar
  const getInitials = (): string => {
    if (userFirstName && userLastName) {
      return `${userFirstName[0]}${userLastName[0]}`.toUpperCase();
    }
    if (userName) {
      return userName.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  // Formatage de la date de création (si disponible)
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Non disponible";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Non disponible";
    }
  };

  interface FormValues {
    first_name: string;
    last_name: string;
    email: string;
    mobile: string;
  }

  const validation = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      first_name: userFirstName || "",
      last_name: userLastName || "",
      email: userEmail || "",
      mobile: userPhone || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("Le prénom est requis"),
      last_name: Yup.string().required("Le nom est requis"),
      email: Yup.string().email("Email invalide").required("L'email est requis"),
      mobile: Yup.string().matches(/^[0-9+\-\s()]*$/, "Format de téléphone invalide"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // TODO: Implémenter l'appel API pour mettre à jour le profil
        // const response = await updateUserProfile(values);
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulation
        toast.success("Profil mis à jour avec succès");
        // Mettre à jour le store si nécessaire
      } catch (error) {
        toast.error("Erreur lors de la mise à jour du profil");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.title = "Profil | Road Alert AGEROUTE";
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center mb-4">
                <h4 className="mb-0">Mon Profil</h4>
              </div>
            </Col>
          </Row>

          <Row>
            {/* Carte d'informations utilisateur */}
            <Col lg="4">
              <Card className="card-ageroute rounded-3 shadow-sm border-0">
                <CardBody className="text-center">
                  <div className="mb-4">
                    <div
                      className="avatar-lg mx-auto mb-3"
                      style={{
                        width: "120px",
                        height: "120px",
                        borderRadius: "50%",
                        backgroundColor: isDarkMode ? "#495057" : "#e9ecef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "3rem",
                        fontWeight: "bold",
                        color: isDarkMode ? "#fff" : "#495057",
                      }}
                    >
                      {getInitials()}
                    </div>
                    <h5 className="mb-1">{userName}</h5>
                    <p className="text-muted mb-0">{userEmail}</p>
                  </div>

                  <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                        <span className="text-muted">ID Utilisateur</span>
                        <Badge color="primary">#{String(userId)}</Badge>
                      </div>
                    {userRoles.length > 0 && (
                      <div className="d-flex flex-column gap-2">
                        <span className="text-muted text-start">Rôles</span>
                        <div className="d-flex flex-wrap gap-2">
                          {userRoles.map((role: any, index: number) => (
                            <Badge 
                              key={index} 
                              color="info" 
                              className="fs-6 px-3 py-2"
                              style={{ fontSize: "1rem", padding: "0.5rem 1rem" }}
                            >
                              {typeof role === "string" ? role : role?.name || "Rôle"}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {userPhone && (
                      <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                        <span className="text-muted">Téléphone</span>
                        <span>{userPhone}</span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Informations de session */}
              <Card className="card-ageroute rounded-3 shadow-sm border-0 mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Informations de session</h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted">Statut</td>
                        <td>
                          <Badge color="success">Connecté</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Dernière connexion</td>
                        <td>{formatDate(user?.lastLogin as string)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Compte créé le</td>
                        <td>{formatDate(user?.createdAt as string)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>

            {/* Formulaire de modification */}
            <Col lg="8">
              <Card className="card-ageroute rounded-3 shadow-sm border-0">
                <CardHeader>
                  <h5 className="card-title mb-0">Modifier mes informations</h5>
                </CardHeader>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="first_name" className="form-label">
                            Prénom <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="first_name"
                            id="first_name"
                            type="text"
                            className="form-control"
                            placeholder="Entrez votre prénom"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.first_name}
                            invalid={
                              validation.touched.first_name && !!validation.errors.first_name
                            }
                          />
                          {validation.touched.first_name && validation.errors.first_name && (
                            <FormFeedback type="invalid">
                              {validation.errors.first_name}
                            </FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="last_name" className="form-label">
                            Nom <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="last_name"
                            id="last_name"
                            type="text"
                            className="form-control"
                            placeholder="Entrez votre nom"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.last_name}
                            invalid={
                              validation.touched.last_name && !!validation.errors.last_name
                            }
                          />
                          {validation.touched.last_name && validation.errors.last_name && (
                            <FormFeedback type="invalid">
                              {validation.errors.last_name}
                            </FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Email <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="email"
                            id="email"
                            type="email"
                            className="form-control"
                            placeholder="Entrez votre email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={validation.touched.email && !!validation.errors.email}
                          />
                          {validation.touched.email && validation.errors.email && (
                            <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="mobile" className="form-label">
                            Téléphone
                          </Label>
                          <Input
                            name="mobile"
                            id="mobile"
                            type="tel"
                            className="form-control"
                            placeholder="Entrez votre numéro de téléphone"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.mobile}
                            invalid={validation.touched.mobile && !!validation.errors.mobile}
                          />
                          {validation.touched.mobile && validation.errors.mobile && (
                            <FormFeedback type="invalid">{validation.errors.mobile}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <div className="mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                        style={{ backgroundColor: "#311B91", borderColor: "#311B91" }}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Mise à jour...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              {/* Informations de compte */}
              <Card className="card-ageroute rounded-3 shadow-sm border-0 mt-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Informations du compte</h5>
                </CardHeader>
                <CardBody>
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "40%" }}>
                          Nom d'utilisateur
                        </td>
                        <td>
                          <strong>{userName}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Email</td>
                        <td>{userEmail || "Non renseigné"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Identifiant</td>
                        <td>#{String(userId)}</td>
                      </tr>
                      {userPhone && (
                        <tr>
                          <td className="text-muted">Téléphone</td>
                          <td>{userPhone}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
